import { useCallback, useEffect, useState } from 'react';
import { Box } from '@coolblue-development/becky';
import EmailEditor from './EmailEditor';
import AttachmentChips from './AttachmentChips';
import { useEmailContext } from '../../../../../../contexts/EmailContext';
import { Alert } from '@coolblue-development/freddy';
import { Attachment } from '../../../../../../models/Attachment';
import { validateAttachments, createAttachmentFromFile } from '../../../../../../utils/attachmentValidation';
import { OutboundEmailFields } from '../../Outbound/EmailOutboundHeader';

interface ReplyInputProps {
  onDiscard: () => void;
  onSuccess: () => void;
  onError: () => void;
  isSendDisabled?: boolean;
  outboundFields?: OutboundEmailFields;
}

const ReplyInput = ({
  onDiscard,
  onSuccess,
  onError,
  isSendDisabled,
  outboundFields
}: ReplyInputProps): JSX.Element => {
  const {
    clearEmailDraft,
    selectedEmail,
    setTemplateHtml,
    emailDrafts = {},
    updateEmailDraft = () => {}
  } = useEmailContext();
  const [bodySizeExceeded, setBodySizeExceeded] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const attachments = selectedEmail ? (emailDrafts[selectedEmail]?.attachments ?? []) : [];

  useEffect(() => {
    setAttachmentError(null);
    setBodySizeExceeded(false);
  }, [selectedEmail]);

  const handleFilesSelected = useCallback(
    (files: FileList) => {
      const result = validateAttachments(files, attachments);

      if (result.error) {
        setAttachmentError(result.error);
      } else {
        setAttachmentError(null);
      }

      if (result.validFiles.length > 0) {
        const newAttachments = result.validFiles.map(createAttachmentFromFile);

        if (selectedEmail) {
          updateEmailDraft(selectedEmail, { attachments: [...attachments, ...newAttachments] });
        }
      }
    },
    [attachments, selectedEmail, updateEmailDraft]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId: string) => {
      if (selectedEmail) {
        updateEmailDraft(selectedEmail, {
          attachments: attachments.filter(attachment => attachment.id !== attachmentId)
        });
      }

      setAttachmentError(null);
    },
    [attachments, selectedEmail, updateEmailDraft]
  );

  const handleDiscard = useCallback(() => {
    setAttachmentError(null);

    if (selectedEmail) {
      clearEmailDraft(selectedEmail);
    } else {
      setTemplateHtml('');
    }

    onDiscard();
  }, [clearEmailDraft, onDiscard, selectedEmail, setTemplateHtml]);

  console.log('atc', { attachments, bodySizeExceeded, attachmentError });

  return (
    <Box
      sx={{
        padding: '12px',
        marginBottom: '16px',
        borderRight: '1px solid #DDDDDD',
        borderLeft: '1px solid #DDDDDD',
        borderBottom: '1px solid #DDDDDD',
        borderRadius: '0 0 4px 4px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <AttachmentChips attachments={attachments} onRemove={handleRemoveAttachment} />

      {bodySizeExceeded && (
        <Alert description="The inserted image is too large and exceeds the size limit of 5MB." intent="error" />
      )}
      {attachmentError && <Alert description={attachmentError} intent="error" />}

      <EmailEditor
        onDiscard={handleDiscard}
        onSuccess={onSuccess}
        onError={onError}
        onBodySizeExceeded={setBodySizeExceeded}
        attachments={attachments}
        onFilesSelected={handleFilesSelected}
        isSendDisabled={isSendDisabled}
        outboundFields={outboundFields}
      />
    </Box>
  );
};

export default ReplyInput;
